import React, { useState } from 'react';
import { Users, Mail, Globe, Heart, CheckCircle, Music, Film, Palette, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import '../styles/FanSignup.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FanSignup = () => {
  const [fanData, setFanData] = useState({
    name: '',
    email: '',
    country: '',
    interests: {
      music: false,
      film: false,
      art: false,
      merchandise: false
    },
    gdpr_consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFanData(prev => ({ ...prev, [name]: value }));
  };

  const handleInterestChange = (interest) => {
    setFanData(prev => ({
      ...prev,
      interests: {
        ...prev.interests,
        [interest]: !prev.interests[interest]
      }
    }));
  };

  const handleConsentChange = () => {
    setFanData(prev => ({ ...prev, gdpr_consent: !prev.gdpr_consent }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!fanData.gdpr_consent) {
      toast.error('Please accept the privacy policy to continue');
      return;
    }

    if (!fanData.name || !fanData.email || !fanData.country) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/fans/`, fanData);
      setIsSuccess(true);
      toast.success('Welcome to the MWP Community!');
      
      // Reset form
      setFanData({
        name: '',
        email: '',
        country: '',
        interests: {
          music: false,
          film: false,
          art: false,
          merchandise: false
        },
        gdpr_consent: false
      });
      
      setTimeout(() => setIsSuccess(false), 5000);
    } catch (error) {
      console.error('Error signing up:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to sign up. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <section id="fan-signup" className="fan-signup-section">
        <div className="fan-signup-container">
          <div className="success-message">
            <CheckCircle className="success-icon" size={64} />
            <h2>Welcome to the MWP Community!</h2>
            <p>Thank you for joining us. You'll be the first to know about new releases, events, and exclusive content.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="fan-signup" className="fan-signup-section" data-animate>
      <div className="fan-signup-container">
        <div className="signup-header">
          <Users className="header-icon" size={48} />
          <h2 className="signup-title">JOIN THE MWP COMMUNITY</h2>
          <p className="signup-subtitle">
            Be part of our journey. Get exclusive updates on new music, film projects, 
            events, and merchandise.
          </p>
        </div>

        <form className="fan-signup-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="fan-name">
                <Users size={18} />
                Name *
              </label>
              <input
                type="text"
                id="fan-name"
                name="name"
                value={fanData.name}
                onChange={handleInputChange}
                placeholder="Your name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="fan-email">
                <Mail size={18} />
                Email *
              </label>
              <input
                type="email"
                id="fan-email"
                name="email"
                value={fanData.email}
                onChange={handleInputChange}
                placeholder="your.email@example.com"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="fan-country">
              <Globe size={18} />
              Country *
            </label>
            <input
              type="text"
              id="fan-country"
              name="country"
              value={fanData.country}
              onChange={handleInputChange}
              placeholder="Your country"
              required
            />
          </div>

          <div className="form-group interests-group">
            <label>
              <Heart size={18} />
              What interests you? *
            </label>
            <div className="interests-grid">
              <button
                type="button"
                className={`interest-btn ${fanData.interests.music ? 'active' : ''}`}
                onClick={() => handleInterestChange('music')}
              >
                <Music size={20} />
                Music
              </button>
              <button
                type="button"
                className={`interest-btn ${fanData.interests.film ? 'active' : ''}`}
                onClick={() => handleInterestChange('film')}
              >
                <Film size={20} />
                Film
              </button>
              <button
                type="button"
                className={`interest-btn ${fanData.interests.art ? 'active' : ''}`}
                onClick={() => handleInterestChange('art')}
              >
                <Palette size={20} />
                Art
              </button>
              <button
                type="button"
                className={`interest-btn ${fanData.interests.merchandise ? 'active' : ''}`}
                onClick={() => handleInterestChange('merchandise')}
              >
                <ShoppingBag size={20} />
                Merchandise
              </button>
            </div>
          </div>

          <div className="consent-group">
            <label className="consent-label">
              <input
                type="checkbox"
                checked={fanData.gdpr_consent}
                onChange={handleConsentChange}
                required
              />
              <span>
                I consent to Made With Purpose collecting and storing my information for 
                updates and communications. You can unsubscribe anytime. *
              </span>
            </label>
          </div>

          <button type="submit" className="btn-primary btn-full" disabled={isSubmitting}>
            <Users size={20} />
            {isSubmitting ? 'Joining...' : 'Join the Community'}
          </button>

          <p className="gdpr-notice">
            We respect your privacy. Your data is stored securely and will never be shared 
            with third parties. Read our <a href="#">Privacy Policy</a>.
          </p>
        </form>
      </div>
    </section>
  );
};

export default FanSignup;